import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { CreateBoardDialog } from '@/features/board-manage';
import {
  useDeletedWorkspaces,
  useMyBoards,
  useMyInvitations,
  useMyRequests,
} from '@/entities/collab';
import {
  activeFilterCount,
  effectiveSort,
  labelChoices,
  matchesFilters,
  useCatalogFilters,
} from '@/entities/catalog';
import { readRecentBoardIds } from '@/entities/board';
import { readRecentDatabaseIds, useMyDatabases } from '@/entities/database';
import { readRecentDocumentIds, useMyDocuments } from '@/entities/document';
import { type HomeWidget, useHomeLayout, useViewer } from '@/entities/viewer';
import { canCreateTasks, useWorkspaces } from '@/entities/workspace';
import { CatalogToolbar } from '@/features/catalog-filters';
import { CREATE_DATABASE, CreateDatabaseDialog } from '@/features/database-catalog';
import { ImportDatabaseDialog } from '@/features/database-portability';
import {
  CreateDocumentDialog,
  ImportDocumentDialog,
  noteDestinations,
} from '@/features/document-catalog';
import { ImportChooser, type ImportChoice } from '@/features/create-menu';
import { QuickCreateDialog } from '@/features/quick-create';
import { HomeCalendarPreview } from '@/features/calendar';
import { taskPath } from '@/entities/my-tasks';
import { originStateFrom } from '@/shared/lib/origin';
import { ErrorState, IconButton, Icons, LoadingState } from '@/shared/ui';

import { BoardCatalog } from './BoardCatalog';
import { CreateMenu } from './CreateMenu';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { HomeCustomizePanel } from './HomeCustomizePanel';
import {
  boardFilterable,
  databaseFilterable,
  documentFilterable,
  HOME_OFFERED,
  HOME_SORTS,
} from './home-filters';
import { HomeDocuments } from './HomeDocuments';
import { HomeGroups } from './HomeGroups';
import { useHomePlaces } from '@/entities/places';
import { HomeDatabases } from './HomeDatabases';
import { homeScope, effectiveFilters } from './home-scope';
import { HomeQuickSearch } from './HomeQuickSearch';
import styles from './HomeDashboard.module.css';
import { MyWorkPanel } from './MyWorkPanel';
import { WorkspaceStrip } from './WorkspaceStrip';

/**
 * The signed-in home, under the unchanged top navigation:
 *
 * 1. page actions - the title, one "Create" menu and ONE search over boards and workspaces;
 * 2. workspaces - the personal one first, then the team workspaces, as compact chips;
 * 3. the board catalog (about three quarters of the width) beside the compact "My work" panel
 *    (the rest); on a phone these stack as search -> workspaces -> my work -> boards.
 *
 * The personal workspace is not presented as a team workspace. Board classification (personal /
 * team / favorites) is exact and a board is listed once whatever grants provide it.
 */
export function HomeDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const viewer = useViewer();
  const boards = useMyBoards({ limit: 1000 });
  const workspaces = useWorkspaces();
  const invitations = useMyInvitations();
  const requests = useMyRequests();
  const deleted = useDeletedWorkspaces();
  const databases = useMyDatabases();
  const documents = useMyDocuments();
  const [query, setQuery] = useState('');
  const { filters: stored, update, reset } = useCatalogFilters();
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [creatingDatabase, setCreatingDatabase] = useState(false);
  const [importingDatabase, setImportingDatabase] = useState(false);
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [importingDocument, setImportingDocument] = useState(false);
  const [choosingImport, setChoosingImport] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const { layout } = useHomeLayout();
  const shown = (id: HomeWidget) => !layout.hidden.includes(id);
  // The search and the filters follow the sections that are shown (hidden ones keep their data).
  const scope = homeScope(layout.hidden);
  const groupsShown = shown('groups');
  const filters = effectiveFilters(stored, scope);
  // `order` positions a section among its siblings; on a phone the zones dissolve (CSS) and the
  // sections stack in exactly the user's order.
  const slot = (id: HomeWidget) => ({ order: layout.order.indexOf(id) });

  const personal = workspaces.data?.find((w) => w.workspace_type === 'personal');
  const teams = useMemo(
    () => (workspaces.data ?? []).filter((w) => w.workspace_type === 'team'),
    [workspaces.data],
  );
  // Where a task can be created: the personal workspace first, then the teams that allow it.
  const taskTargets = useMemo(
    () =>
      [...(personal ? [personal] : []), ...teams.filter((w) => canCreateTasks(w))].map((w) => ({
        id: w.id,
        name: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
      })),
    [personal, teams, t],
  );
  const databaseTargets = useMemo(
    () => (workspaces.data ?? []).filter((w) => w.permissions.includes(CREATE_DATABASE)),
    [workspaces.data],
  );

  const documentTargets = useMemo(() => noteDestinations(workspaces.data ?? []), [workspaces.data]);
  const places = useHomePlaces(workspaces.data ?? []);
  // The label filter offers the labels the viewer actually sees on boards, databases, notes, groups.
  const workspaceNames = useMemo(
    () =>
      new Map(
        (workspaces.data ?? []).map((w) => [
          w.id,
          w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
        ]),
      ),
    [workspaces.data, t],
  );
  const labelChoicesList = useMemo(() => {
    const found: { id: string; name: string; color: string; workspace: string | null }[] = [];
    const offers = (type: 'board' | 'database' | 'document') => scope.types.includes(type);
    // Labels belong to one workspace: the workspace name tells equal names apart.
    const named = (id: string | null | undefined) => (id ? (workspaceNames.get(id) ?? null) : null);
    const take = (
      labels: readonly { id: string; name: string; color: string }[] | undefined,
      workspaceId: string | null | undefined,
    ) => {
      for (const label of labels ?? []) found.push({ ...label, workspace: named(workspaceId) });
    };
    if (offers('board')) for (const b of boards.data ?? []) take(b.labels, b.workspace_id);
    if (offers('database')) for (const d of databases.data ?? []) take(d.labels, d.workspace_id);
    if (offers('document')) for (const d of documents.data ?? []) take(d.labels, d.workspace_id);
    if (groupsShown) for (const g of places.groups) take(g.labels, g.workspace_id);
    return labelChoices(found, i18n.language);
  }, [
    boards.data,
    databases.data,
    documents.data,
    places.groups,
    scope,
    groupsShown,
    workspaceNames,
    i18n.language,
  ]);

  // How many of each type the OTHER filters and the search leave (the badges of the type control).
  const counts = useMemo(() => {
    const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
    const anyType = { ...filters, type: 'all' as const };
    const openedBoards = new Set(userId ? readRecentBoardIds(userId) : []);
    const openedDatabases = new Set(userId ? readRecentDatabaseIds(userId) : []);
    const spaces = new Map((workspaces.data ?? []).map((w) => [w.id, w.workspace_type] as const));
    const names = new Map(
      (workspaces.data ?? []).map(
        (w) =>
          [w.id, w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name] as const,
      ),
    );
    const board = (boards.data ?? []).filter((item) =>
      matchesFilters(boardFilterable(item, openedBoards), anyType, userId, query, HOME_OFFERED),
    ).length;
    const database = (databases.data ?? []).filter((item) =>
      matchesFilters(
        databaseFilterable(item, openedDatabases, spaces, names),
        anyType,
        userId,
        query,
        HOME_OFFERED,
      ),
    ).length;
    const openedDocuments = new Set(userId ? readRecentDocumentIds(userId) : []);
    const document = (documents.data ?? []).filter((item) =>
      matchesFilters(
        documentFilterable(item, openedDocuments, spaces, places.locationOf(item)),
        anyType,
        userId,
        query,
        HOME_OFFERED,
      ),
    ).length;
    const offered = (type: 'board' | 'database' | 'document', n: number) =>
      scope.types.includes(type) ? n : 0;
    return {
      all: offered('board', board) + offered('database', database) + offered('document', document),
      board,
      database,
      document,
    };
  }, [
    filters,
    query,
    boards.data,
    databases.data,
    documents.data,
    workspaces.data,
    viewer,
    t,
    places,
    scope,
  ]);

  const importChoices: ImportChoice[] = [];
  if (databaseTargets.length > 0) {
    importChoices.push({
      id: 'database',
      title: t('import.database.title'),
      hint: t('import.database.hint'),
      icon: <Icons.layers />,
      onSelect: () => {
        setImportingDatabase(true);
      },
    });
  }
  if (documentTargets.length > 0) {
    importChoices.push({
      id: 'document',
      title: t('import.document.title'),
      hint: t('import.document.hint'),
      icon: <Icons.document />,
      onSelect: () => {
        setImportingDocument(true);
      },
    });
  }

  if (boards.isPending || workspaces.isPending) return <LoadingState />;
  if (boards.isError)
    return <ErrorState error={boards.error} onRetry={() => void boards.refetch()} />;
  if (workspaces.isError) {
    return <ErrorState error={workspaces.error} onRetry={() => void workspaces.refetch()} />;
  }
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  // Useful by default: what the viewer opened lately, else what changed lately.
  const openedAny = user ? readRecentBoardIds(user.id).length > 0 : false;
  const sort = effectiveSort(filters, HOME_SORTS, openedAny ? 'opened' : 'updated');
  // Nothing to filter (no list shown, or no resources at all and no filter set): no filter element.
  const showFilters =
    scope.filterable &&
    (counts.all > 0 || query.trim() !== '' || activeFilterCount(filters, HOME_OFFERED) > 0);
  const waiting = (invitations.data?.length ?? 0) + (requests.data?.length ?? 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.titles}>
          <h1 className={styles.title}>{t('home.title')}</h1>
          <p className={styles.subtitle}>{t('home.subtitle')}</p>
        </div>
        <div className={styles.actions} role="group" aria-label={t('home.actions')}>
          <IconButton
            label={t('home.customize.open')}
            icon={<Icons.sliders />}
            aria-pressed={customizing}
            onClick={() => {
              setCustomizing((open) => !open);
            }}
          />
          <CreateMenu
            personal={personal}
            canCreateTask={taskTargets.length > 0}
            onNewTask={() => {
              setCreatingTask(true);
            }}
            onNewBoard={() => {
              setCreatingBoard(true);
            }}
            {...(databaseTargets.length > 0 && {
              onNewDatabase: () => {
                setCreatingDatabase(true);
              },
            })}
            {...(documentTargets.length > 0 && {
              onNewDocument: () => {
                setCreatingDocument(true);
              },
            })}
            {...(importChoices.length > 0 && {
              onImport: () => {
                setChoosingImport(true);
              },
            })}
            onNewWorkspace={() => {
              setCreatingWorkspace(true);
            }}
          />
        </div>
      </header>

      {customizing && (
        <HomeCustomizePanel
          onDone={() => {
            setCustomizing(false);
          }}
        />
      )}

      {scope.searchable && (
        <>
          <CatalogToolbar
            part="search"
            query={query}
            onQuery={setQuery}
            searchLabel={t('home.searchAll')}
            filters={filters}
            onChange={update}
            onReset={reset}
            offered={HOME_OFFERED}
            sorts={scope.sorts}
            sort={sort}
          />
          {showFilters && (
            <CatalogToolbar
              part="filters"
              query={query}
              onQuery={setQuery}
              searchLabel={t('home.searchAll')}
              filters={filters}
              onChange={update}
              onReset={reset}
              offered={{ ...HOME_OFFERED, type: scope.types.length > 2 }}
              sorts={scope.sorts}
              sort={sort}
              counts={counts}
              labelChoices={labelChoicesList}
              typeChoices={scope.types}
            />
          )}
          <HomeQuickSearch
            query={query}
            workspaces={workspaces.data}
            boards={boards.data}
            databases={databases.data ?? []}
            documents={documents.data ?? []}
          />
        </>
      )}

      {waiting > 0 && (
        <Link to="/app/invitations" className={styles.notice} data-testid="invitations-link">
          <Icons.mail />
          <span>{t('home.invitationsWaiting', { count: waiting })}</span>
          <Icons.arrowRight />
        </Link>
      )}

      <div
        className={styles.main}
        data-wide={(!shown('my_work') && !shown('calendar')) || undefined}
      >
        <div className={styles.mainSide}>
          {shown('my_work') && (
            <div className={styles.widget} style={slot('my_work')} data-widget="my_work">
              <MyWorkPanel />
            </div>
          )}
        </div>
        <div className={styles.mainCatalog} data-testid="home-catalog">
          {shown('workspaces') && (
            <div className={styles.widget} style={slot('workspaces')} data-widget="workspaces">
              <WorkspaceStrip
                personal={personal}
                teams={teams}
                query={query}
                filters={filters}
                userId={user?.id ?? null}
                trashCount={(deleted.data ?? []).length}
              />
            </div>
          )}
          {shown('groups') && (
            <div className={styles.widget} style={slot('groups')} data-widget="groups">
              <HomeGroups
                places={places}
                boards={boards.data}
                databases={databases.data ?? []}
                documents={documents.data ?? []}
                query={query}
                filters={filters}
                userId={user?.id ?? null}
              />
            </div>
          )}
          {shown('boards') && (filters.type === 'all' || filters.type === 'board') && (
            <div className={styles.widget} style={slot('boards')} data-widget="boards">
              <BoardCatalog
                boards={boards.data}
                query={query}
                filters={filters}
                sort={sort}
                userId={user?.id ?? null}
                locale={i18n.language}
              />
            </div>
          )}
          {shown('databases') && (
            <div className={styles.widget} style={slot('databases')} data-widget="databases">
              <HomeDatabases
                workspaces={workspaces.data}
                query={query}
                filters={filters}
                sort={sort}
                userId={user?.id ?? null}
                locale={i18n.language}
              />
            </div>
          )}
          {shown('documents') && (
            <div className={styles.widget} style={slot('documents')} data-widget="documents">
              <HomeDocuments
                documents={documents.data ?? []}
                places={places}
                query={query}
                filters={filters}
                sort={sort}
                userId={user?.id ?? null}
                locale={i18n.language}
              />
            </div>
          )}
        </div>
        <div className={styles.mainCalendar}>
          {shown('calendar') && (
            <div className={styles.widget} style={slot('calendar')} data-widget="calendar">
              <HomeCalendarPreview
                boards={boards.data}
                timeZone={user?.timezone ?? null}
                locale={i18n.language}
                calendarPath="/app/calendar"
                onOpenCard={(cardId, boardId) => void navigate(taskPath(boardId, cardId))}
              />
            </div>
          )}
        </div>
      </div>

      {creatingDatabase && (
        <CreateDatabaseDialog
          workspaces={databaseTargets}
          onCreated={(database) => {
            setCreatingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingDatabase(false);
          }}
        />
      )}
      {choosingImport && (
        <ImportChooser
          choices={importChoices}
          onClose={() => {
            setChoosingImport(false);
          }}
        />
      )}
      {creatingDocument && (
        <CreateDocumentDialog
          workspaces={documentTargets}
          onCreated={(document) => {
            setCreatingDocument(false);
            void navigate(`/app/documents/${document.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingDocument(false);
          }}
        />
      )}
      {importingDocument && (
        <ImportDocumentDialog
          workspaces={documentTargets}
          onCreated={(document) => {
            setImportingDocument(false);
            void navigate(`/app/documents/${document.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setImportingDocument(false);
          }}
        />
      )}
      {importingDatabase && (
        <ImportDatabaseDialog
          workspaces={databaseTargets}
          onImported={(database) => {
            setImportingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setImportingDatabase(false);
          }}
        />
      )}
      {creatingWorkspace && (
        <CreateWorkspaceDialog
          onCreated={(workspace) => {
            setCreatingWorkspace(false);
            void navigate(`/app/workspaces/${workspace.id}`);
          }}
          onClose={() => {
            setCreatingWorkspace(false);
          }}
        />
      )}
      {creatingBoard && personal && (
        <CreateBoardDialog
          workspaceId={personal.id}
          onCreated={(created) => {
            setCreatingBoard(false);
            void navigate(`/app/boards/${created.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingBoard(false);
          }}
        />
      )}
      {creatingTask && taskTargets[0] && (
        <QuickCreateDialog
          workspaceId={taskTargets[0].id}
          workspaces={taskTargets}
          timezone={user?.timezone ?? null}
          onClose={() => {
            setCreatingTask(false);
          }}
        />
      )}
    </div>
  );
}
